import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import type { Locale } from '@siteshowroom/shared'
import { MediaGrid } from '../components/media-grid'
import { MediaUploader } from '../components/media-uploader'
import { SpacesSection } from '../components/spaces-section'
import { useLocale, useTranslate } from '../lib/i18n-provider'
import {
  deleteMedia,
  listMedia,
  listResidences,
  mediaErrorMessageKey,
  reorderMedia,
  saveMediaText,
  setPrimaryMedia,
  updateMedia,
} from '../lib/media-client'
import type {
  MediaAsset,
  MediaCategory,
  MediaFailure,
  MediaResult,
  MediaText,
  ResidenceOption,
} from '../lib/media-client'
import {
  assignSpace,
  createSpace,
  deleteSpace,
  listSpaces,
  updateSpace,
} from '../lib/spaces-client'
import { spaceErrorMessageKey } from '../lib/spaces-client'
import type { Space, SpaceFailure, SpaceKind, SpaceResult } from '../lib/spaces-client'

/**
 * Het beheerscherm voor de foto's van één residence.
 *
 * De pagina doet drie dingen en houdt ze uit elkaar: kiezen welke residence het
 * betreft, foto's aanleveren, en de aangeleverde foto's ordenen en beschrijven.
 *
 * Na iedere wijziging wordt de lijst opnieuw opgehaald in plaats van lokaal
 * bijgewerkt. Dat is één aanvraag meer, maar de API is de bron: `sortOrder`
 * verschuift bij een herordening voor álle foto's, en de hoofdfoto zetten haalt
 * de vorige hoofdfoto onderuit — beide via een databasegarantie die het scherm
 * niet hoort na te bootsen (CLAUDE.md regel 2).
 */

type ResidencesState =
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly residences: readonly ResidenceOption[] }
  | { readonly phase: 'error'; readonly error: MediaFailure }

type AssetsState =
  | { readonly phase: 'idle' }
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly assets: readonly MediaAsset[] }
  | { readonly phase: 'error'; readonly error: MediaFailure }

/** Vertaalde melding plus het onvertaalde technische detail ernaast. */
function FailureNotice({ error }: { readonly error: MediaFailure }): ReactElement {
  const t = useTranslate()
  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__title">{t(mediaErrorMessageKey(error))}</p>
      {/* Diagnose-informatie, bewust onvertaald — zelfde keuze als op de systeempagina. */}
      <code className="notice__detail">{error.message}</code>
    </div>
  )
}

/** Zelfde opbouw als `FailureNotice`, met de woordenlijst van de ruimtes. */
function SpaceFailureNotice({ error }: { readonly error: SpaceFailure }): ReactElement {
  const t = useTranslate()
  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__title">{t(spaceErrorMessageKey(error))}</p>
      <code className="notice__detail">{error.message}</code>
    </div>
  )
}

export function MediaPage(): ReactElement {
  const t = useTranslate()
  const { locales } = useLocale()

  const [residencesState, setResidencesState] = useState<ResidencesState>({ phase: 'loading' })
  const [residenceId, setResidenceId] = useState<string | null>(null)
  const [assetsState, setAssetsState] = useState<AssetsState>({ phase: 'idle' })
  // De ruimtes staan los van de foto's: een mislukte ruimtelijst mag het
  // fotobeheer niet blokkeren, dus geen gedeelde toestandsmachine maar een
  // eenvoudige lijst die leeg blijft wanneer het ophalen misging.
  const [spaces, setSpaces] = useState<readonly Space[]>([])
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<MediaFailure | null>(null)
  // Een eigen foutstaat, want de foutcodes van de ruimtes zijn een andere
  // woordenlijst dan die van de media-routes en verdienen hun eigen melding.
  const [spaceError, setSpaceError] = useState<SpaceFailure | null>(null)

  const residencesControllerRef = useRef<AbortController | null>(null)
  const assetsControllerRef = useRef<AbortController | null>(null)
  const spacesControllerRef = useRef<AbortController | null>(null)

  const loadResidences = useCallback(async (): Promise<void> => {
    residencesControllerRef.current?.abort()
    const controller = new AbortController()
    residencesControllerRef.current = controller

    const result = await listResidences({ signal: controller.signal })
    if (controller.signal.aborted) {
      return
    }
    if (!result.ok) {
      setResidencesState({ phase: 'error', error: result.error })
      return
    }
    setResidencesState({ phase: 'ready', residences: result.data })
    // De eerste residence volstaat als beginkeuze; wie er meer heeft, kiest zelf.
    const first = result.data[0]
    setResidenceId(first === undefined ? null : first.id)
  }, [])

  const loadAssets = useCallback(async (): Promise<void> => {
    if (residenceId === null) {
      setAssetsState({ phase: 'idle' })
      return
    }
    // Een lopende aanvraag afbreken voorkomt dat een traag antwoord een nieuwer
    // antwoord overschrijft.
    assetsControllerRef.current?.abort()
    const controller = new AbortController()
    assetsControllerRef.current = controller

    const result = await listMedia(residenceId, { signal: controller.signal })
    if (controller.signal.aborted) {
      return
    }
    if (result.ok) {
      setAssetsState({ phase: 'ready', assets: result.data })
    } else {
      setAssetsState({ phase: 'error', error: result.error })
    }
  }, [residenceId])

  const loadSpaces = useCallback(async (): Promise<void> => {
    if (residenceId === null) {
      setSpaces([])
      return
    }
    spacesControllerRef.current?.abort()
    const controller = new AbortController()
    spacesControllerRef.current = controller

    const result = await listSpaces(residenceId, { signal: controller.signal })
    if (controller.signal.aborted) {
      return
    }
    setSpaces(result.ok ? result.data : [])
  }, [residenceId])

  useEffect(() => {
    // Zie de toelichting op hetzelfde patroon in system-page.tsx: state wordt pas
    // ná de await gezet, de begintoestand is al 'loading', en de cleanup breekt de
    // aanvraag af. De lintregel kan dat statisch niet zien.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadResidences()
    return () => {
      residencesControllerRef.current?.abort()
    }
  }, [loadResidences])

  useEffect(() => {
    if (residenceId === null) {
      return
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAssetsState({ phase: 'loading' })
    void loadAssets()
    void loadSpaces()
    return () => {
      assetsControllerRef.current?.abort()
      spacesControllerRef.current?.abort()
    }
  }, [residenceId, loadAssets, loadSpaces])

  /**
   * Voert één wijziging uit en haalt daarna de lijst opnieuw op — ook wanneer de
   * wijziging mislukte. Anders blijft er een scherm staan dat een toestand toont
   * die de database niet heeft.
   */
  const runMutation = useCallback(
    async (run: () => Promise<MediaResult<unknown>>): Promise<void> => {
      setBusy(true)
      setActionError(null)
      const result = await run()
      if (!result.ok) {
        setActionError(result.error)
      }
      await loadAssets()
      setBusy(false)
    },
    [loadAssets],
  )

  /**
   * Zelfde als `runMutation`, maar haalt ook de ruimtes opnieuw op.
   *
   * Elke wijziging aan een ruimte raakt beide lijsten: een foto indelen verandert
   * ook zijn categorie, en een ruimte verwijderen maakt foto's los. Alleen de ene
   * of de andere lijst verversen laat een scherm achter dat een toestand toont die
   * de database niet heeft.
   */
  const runSpaceMutation = useCallback(
    async (run: () => Promise<SpaceResult<unknown>>): Promise<void> => {
      setBusy(true)
      setSpaceError(null)
      const result = await run()
      if (!result.ok) {
        setSpaceError(result.error)
      }
      await loadSpaces()
      await loadAssets()
      setBusy(false)
    },
    [loadAssets, loadSpaces],
  )

  function handleCreateSpace(kind: SpaceKind, label: string): void {
    if (residenceId === null) {
      return
    }
    void runSpaceMutation(() => createSpace(residenceId, { kind, label }))
  }

  function handleRenameSpace(spaceId: string, label: string): void {
    if (residenceId === null) {
      return
    }
    void runSpaceMutation(() => updateSpace(residenceId, spaceId, { label }))
  }

  function handleDeleteSpace(space: Space): void {
    if (residenceId === null) {
      return
    }
    // Verwijderen kost geen foto's — ze raken alleen hun indeling kwijt. Het aantal
    // staat in de vraag, want dat is precies wat iemand wil weten voordat hij een
    // indeling herziet.
    const question =
      space.photoCount === 0
        ? t('dashboard.spaces.confirmDelete.empty')
        : t('dashboard.spaces.confirmDelete.withPhotos', { count: space.photoCount })
    if (!window.confirm(question)) {
      return
    }
    void runSpaceMutation(() => deleteSpace(residenceId, space.id))
  }

  function handleAssignSpace(assetId: string, spaceId: string | null): void {
    if (residenceId === null) {
      return
    }
    void runSpaceMutation(() => assignSpace(residenceId, assetId, spaceId))
  }

  function handleSetPrimary(assetId: string): void {
    if (residenceId === null) {
      return
    }
    void runMutation(() => setPrimaryMedia(residenceId, assetId))
  }

  function handleChangeCategory(assetId: string, category: MediaCategory): void {
    if (residenceId === null) {
      return
    }
    void runMutation(() => updateMedia(residenceId, assetId, { category }))
  }

  function handleSaveText(assetId: string, locale: Locale, text: MediaText): void {
    if (residenceId === null) {
      return
    }
    void runMutation(() => saveMediaText(residenceId, assetId, locale, text))
  }

  function handleReorder(assetIds: readonly string[]): void {
    if (residenceId === null) {
      return
    }
    void runMutation(() => reorderMedia(residenceId, assetIds))
  }

  function handleDelete(assetId: string): void {
    if (residenceId === null) {
      return
    }
    void runMutation(() => deleteMedia(residenceId, assetId))
  }

  const handleUploaded = useCallback((): void => {
    void loadAssets()
  }, [loadAssets])

  return (
    <section className="page" aria-labelledby="media-heading">
      <div className="page__header">
        <h1 className="page__title" id="media-heading">
          {t('dashboard.media.title')}
        </h1>
        <button
          className="button"
          disabled={residenceId === null || assetsState.phase === 'loading' || busy}
          onClick={() => {
            setAssetsState({ phase: 'loading' })
            void loadAssets()
          }}
          type="button"
        >
          {t('common.action.reload')}
        </button>
      </div>

      {residencesState.phase === 'loading' && (
        <p className="page__status" role="status">
          {t('common.state.loading')}
        </p>
      )}

      {residencesState.phase === 'error' && <FailureNotice error={residencesState.error} />}

      {residencesState.phase === 'ready' && residencesState.residences.length === 0 && (
        <p className="page__status" role="status">
          {t('dashboard.media.noResidence')}
        </p>
      )}

      {/* De keuzelijst verschijnt pas bij meer dan één residence; bij precies één
          is er niets te kiezen en zou hij alleen ruis zijn. */}
      {residencesState.phase === 'ready' && residencesState.residences.length > 1 && (
        <label className="field field--inline">
          <span className="field__label">{t('dashboard.media.residence')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              setResidenceId(event.target.value)
            }}
            value={residenceId ?? ''}
          >
            {residencesState.residences.map((residence) => (
              <option key={residence.id} value={residence.id}>
                {/* Naam uit de database: data, geen interfacetekst. */}
                {residence.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {residenceId !== null && (
        <>
          {spaceError !== null && <SpaceFailureNotice error={spaceError} />}

          <SpacesSection
            busy={busy}
            onCreate={handleCreateSpace}
            onDelete={handleDeleteSpace}
            onRename={handleRenameSpace}
            spaces={spaces}
          />

          <MediaUploader onUploaded={handleUploaded} residenceId={residenceId} />

          {actionError !== null && <FailureNotice error={actionError} />}

          {assetsState.phase === 'loading' && (
            <p className="page__status" role="status">
              {t('common.state.loading')}
            </p>
          )}

          {assetsState.phase === 'error' && <FailureNotice error={assetsState.error} />}

          {assetsState.phase === 'ready' && (
            <MediaGrid
              assets={assetsState.assets}
              busy={busy}
              locales={locales}
              onAssignSpace={handleAssignSpace}
              onChangeCategory={handleChangeCategory}
              onDelete={handleDelete}
              onReorder={handleReorder}
              onSaveText={handleSaveText}
              onSetPrimary={handleSetPrimary}
              spaces={spaces}
            />
          )}
        </>
      )}
    </section>
  )
}
